<template>
    <div
        v-if="isError"
        class="box-body with-border clearfix"
    >
        <div class="row">
            <div class="col-xs-12">
                <p>
                    {{ $t('messages.list.category.tag.noTag', { id: tagId }) }}
                </p>
            </div>
        </div>
    </div>

    <ValidationObserver
        v-else
        ref="observer"
        v-slot="{ invalid }"
        tag="div"
    >
        <div class="box-body">
            <div class="row">
                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <ValidationProvider
                        v-slot="{ errors }"
                        name="name"
                        rules="required"
                        slim
                    >
                        <div
                            :class="{ 'has-error': errors.length }"
                            class="form-group"
                        >
                            <label>{{ $t('name') }} <b class="text-danger">*</b></label>

                            <input
                                v-model="tag.name"
                                :disabled="isLoading"
                                :placeholder="`${$t('enter')} ${$t('name')}`"
                                class="form-control"
                                type="text"
                            />

                            <div
                                v-if="errors.length"
                                class="help-block"
                                v-text="errors[0]"
                            />
                        </div>
                    </ValidationProvider>
                </div>

                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <div class="form-group">
                        <label>{{ $t('active') }}</label>

                        <FormSwitcher
                            v-model="tag.active"
                            :disabled="isLoading"
                        />
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <div class="form-group">
                        <label>{{ $t('listCategory.categories') }}</label>

                        <FormSelect
                            v-model="tag.categories"
                            :options="categories"
                            :placeholder="`${$t('select')} ${$t('listCategory.categories')}`"
                            allowEmpty
                            label="name"
                            multiple
                            searchable
                            trackBy="id"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="box-footer">
            <div class="row">
                <div class="col-xs-12">
                    <button
                        :disabled="isLoading || invalid"
                        class="btn btn-sm btn-primary"
                        type="button"
                        @click="submitTag"
                    >
                        <i
                            v-if="isLoading"
                            class="fa fa-refresh fa-spin"
                        />
                        <span>{{ isEdit ? 'Update' : 'Create' }}</span>
                    </button>

                    <button
                        :disabled="isLoading"
                        class="btn btn-sm btn-default"
                        @click="
                            $router.push({
                                name: 'contacts-lists.categories.tags.list',
                            })
                        "
                    >
                        {{ $t('cancel') }}
                    </button>
                </div>
            </div>
        </div>
    </ValidationObserver>
</template>

<script>
import EventBus from '@/event-bus';
import FormSwitcher from '@/components/form/FormSwitcher';

export default {
    components: {
        FormSwitcher,
    },
    props: {
        tagId: {
            type: Number,
            default: undefined,
        },
    },
    data() {
        return {
            isError: false,
            isLoading: false,
            tag: {
                name: '',
                active: true,
                categories: [],
            },
            categories: [],
        };
    },
    computed: {
        isEdit() {
            return !!this.tagId;
        },
    },
    async created() {
        await this.fetchTag();
        this.$emit('loaded', this.tag);
    },

    methods: {
        async fetchTag() {
            try {
                this.isLoading = true;

                const {
                    data: { tag, categories },
                } = await axios.get(`/lists/categories/tags/data/${this.tagId || 0}`);

                if (!Array.isArray(tag)) {
                    this.tag = tag;
                }

                this.categories = categories;
            } catch (error) {
                this.isError = true;
            } finally {
                this.isLoading = false;
            }
        },

        async submitTag() {
            if (!(await this.$refs.observer.validate())) {
                return;
            }

            try {
                this.isLoading = true;

                const url = this.isEdit ? `/lists/categories/tags/data/${this.tagId}` : '/lists/categories/tags/data';
                const data = {
                    ...this.tag,
                    categories: this.tag.categories.map((category) => category.id),
                };

                const {
                    data: { action, tag },
                } = await axios({
                    method: this.isEdit ? 'PUT' : 'POST',
                    url,
                    data,
                });

                if (action === 'created') {
                    this.onCreated(tag.name);
                } else if (action === 'updated') {
                    this.onUpdated(tag.id, tag.name);
                }
            } catch (error) {
                const {
                    data: { errors, message },
                } = error.response;

                if (errors) {
                    this.$refs.observer.setErrors(errors);
                }

                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message,
                    errors,
                });
            } finally {
                this.isLoading = false;
            }
        },

        async onUpdated(id, name) {
            if (!id && !name) return;

            await this.$router.push({
                name: 'contacts-lists.categories.tags.show',
                id,
            });

            EventBus.$emit('alertData', {
                class: 'alert-success',
                message: this.$t('messages.list.category.tag.updated', { name }),
            });
        },

        async onCreated(name) {
            if (!name) return;

            await this.$router.push({
                name: 'contacts-lists.categories.tags.list',
            });

            EventBus.$emit('alertData', {
                class: 'alert-success',
                message: this.$t('messages.list.category.tag.created', { name }),
            });
        },
    },
};
</script>
